import React, { useState } from 'react';

import Header from '../../components/ui/Header';
import ShoppingCart from '../../components/ui/ShoppingCart';
import ContactHub from '../../components/ui/ContactHub';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ServicesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Service categories
  const categories = [
    { id: 'all', label: 'All Services', icon: 'Grid3x3' },
    { id: 'residential', label: 'Residential', icon: 'Home' },
    { id: 'commercial', label: 'Commercial', icon: 'Building' },
    { id: 'specialized', label: 'Specialized', icon: 'Wrench' }
  ];

  // All services data
  const allServices = [
    {
      id: 'house-cleaning',
      title: 'House Cleaning',
      category: 'residential',
      icon: 'Home',
      description: 'Regular house cleaning services to keep your home spotless and fresh.',
      features: ['Weekly/Bi-weekly/Monthly', 'Eco-friendly products', 'Insured & bonded'],
      rating: 4.9,
      reviewCount: 524,
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      isPopular: true
    },
    {
      id: 'office-cleaning',
      title: 'Office Cleaning',
      category: 'commercial',
      icon: 'Building',
      description: 'Professional office cleaning services for businesses of all sizes.',
      features: ['After-hours service', 'Flexible scheduling', 'Commercial grade equipment'],
      rating: 4.8,
      reviewCount: 312,
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop',
      isPopular: false
    },
    {
      id: 'carpet-cleaning',
      title: 'Carpet Cleaning',
      category: 'specialized',
      icon: 'Square',
      description: 'Deep carpet cleaning to remove stains, odors, and allergens effectively.',
      features: ['Steam cleaning', 'Stain protection', 'Pet odor removal'],
      rating: 4.9,
      reviewCount: 428,
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
      isPopular: true
    },
    {
      id: 'deep-cleaning',
      title: 'Deep Cleaning',
      category: 'residential',
      icon: 'Sparkles',
      description: 'Comprehensive deep cleaning for move-ins, seasonal cleaning, and special occasions.',
      features: ['Top to bottom clean', 'Inside appliances', 'Detail oriented'],
      rating: 4.8,
      reviewCount: 267,
      image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=400&h=300&fit=crop',
      isPopular: true
    },
    {
      id: 'post-construction',
      title: 'Post-Construction Cleanup',
      category: 'specialized',
      icon: 'HardHat',
      description: 'Specialized cleaning after construction or renovation projects.',
      features: ['Dust removal', 'Debris cleanup', 'Window cleaning'],
      rating: 4.7,
      reviewCount: 189,
      image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop',
      isPopular: false
    },
    {
      id: 'disinfection',
      title: 'Disinfection Services',
      category: 'specialized',
      icon: 'Shield',
      description: 'Professional disinfection services using hospital-grade sanitizers.',
      features: ['EPA approved products', 'Virus protection', 'Safe for families'],
      rating: 4.9,
      reviewCount: 156,
      image: 'https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=400&h=300&fit=crop',
      isPopular: false
    }
  ];

  // Filter services based on selected category
  const filteredServices = selectedCategory === 'all' 
    ? allServices 
    : allServices?.filter(service => service?.category === selectedCategory);

  const handleServiceClick = (serviceId) => {
    // Navigate to service details page
    window.location.href = `/service-details/${serviceId}`;
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={`${
          index < Math.floor(rating)
            ? 'text-yellow-400 fill-current' :'text-gray-300'
        }`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <div className="pt-16 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-6">
              Our Cleaning Services
            </h1>
            <p className="text-xl text-text-secondary mb-8 max-w-3xl mx-auto">
              Professional cleaning services tailored to your needs. From regular house cleaning to specialized commercial services, we've got you covered.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Phone"
                iconPosition="left"
              >
                Get Free Quote
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
              >
                Schedule Service
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap justify-center gap-4">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                selectedCategory === category?.id
                  ? 'bg-primary text-white shadow-elevation-1'
                  : 'bg-white text-text-secondary hover:text-text-primary hover:bg-muted border border-border'
              }`}
            >
              <Icon name={category?.icon} size={18} />
              <span>{category?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredServices?.map((service) => (
            <div
              key={service?.id}
              className="bg-white rounded-xl shadow-elevation-2 overflow-hidden hover:shadow-elevation-3 transition-all duration-300 cursor-pointer group"
              onClick={() => handleServiceClick(service?.id)}
            >
              {/* Service Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service?.image}
                  alt={service?.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                {service?.isPopular && (
                  <div className="absolute top-4 left-4 bg-primary text-white px-3 py-1 rounded-full text-xs font-medium">
                    Popular
                  </div>
                )}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-lg">
                  <Icon name={service?.icon} size={20} className="text-primary" />
                </div>
              </div>

              {/* Service Content */}
              <div className="p-6">
                <div className="mb-4">
                  <h3 className="text-xl font-semibold text-text-primary mb-2 group-hover:text-primary transition-colors">
                    {service?.title}
                  </h3>
                  <p className="text-text-secondary text-sm line-clamp-2">
                    {service?.description}
                  </p>
                </div>

                {/* Features */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {service?.features?.slice(0, 3)?.map((feature, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-muted text-text-secondary text-xs rounded-md"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex">{renderStars(service?.rating)}</div>
                  <span className="text-sm font-medium text-text-primary">
                    {service?.rating}
                  </span>
                  <span className="text-sm text-text-secondary">
                    ({service?.reviewCount} reviews)
                  </span>
                </div>

                {/* CTA Button */}
                <div className="mt-4">
                  <Button
                    variant="outline"
                    fullWidth
                    size="sm"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={(e) => {
                      e?.stopPropagation();
                      handleServiceClick(service?.id);
                    }}
                  >
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Why Choose Us Section */}
      <div className="bg-muted py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-text-primary mb-4">
              Why Choose Clean360?
            </h2>
            <p className="text-lg text-text-secondary">
              Professional cleaning services you can trust
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-2">
                100% Satisfaction Guaranteed
              </h3>
              <p className="text-text-secondary">
                We're not happy unless you're happy. 30-day satisfaction guarantee on all services.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Shield" size={32} className="text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-2">
                Insured & Bonded
              </h3>
              <p className="text-text-secondary">
                Fully insured and bonded for your peace of mind. Your property is protected.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Users" size={32} className="text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary mb-2">
                Trained Professionals
              </h3>
              <p className="text-text-secondary">
                Background-checked, trained professionals who care about quality and details.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* CTA Section */}
      <div className="bg-primary py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Book your cleaning service today and experience the Clean360 difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              iconName="Phone"
              iconPosition="left"
            >
              Call (555) 123-4567
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              className="border-white text-white hover:bg-white hover:text-primary"
            >
              Book Online
            </Button>
          </div>
        </div>
      </div>
      {/* Global Components */}
      <ShoppingCart />
      <ContactHub />
      {/* Mobile Spacing */}
      <div className="h-20 lg:hidden" />
    </div>
  );
};

export default ServicesPage;